import { useCreate, useTranslate } from "@refinedev/core";
import { UserInviteResponse, Workspace } from "types";
import { useAntTheme } from "hooks/useAntTheme";
import { Button, Flex, Input, Modal, Typography } from "antd";
import { FeatureFlags, USER_INVITE_EXPIRY_DAYS } from "@/constants";
import { useFeature } from "@/hooks/useFeature";
import { WorkspaceInvite } from "./WorkspaceInvite";
import { useState } from "react";
import dayjs from "dayjs";
import { isValidEmail } from "helpers/inputValidation/emailValidation/";
import { useQuota } from "hooks/useQuota";

interface CreateWorkspaceModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  quotaLimit: number;
  usageCount: number;
  organizationId?: string;
}

export const CreateWorkspaceModal = ({
  open,
  onCancel,
  onSuccess,
  quotaLimit,
  usageCount,
  organizationId,
}: CreateWorkspaceModalProps) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const { refetch: refetchQuota } = useQuota();
  const { isEnabled: isWorkspaceInvitesEnabled } = useFeature(
    FeatureFlags.WorkspaceInvites
  );
  const [workspaceName, setWorkspaceName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteUsers, setInviteUsers] = useState<Array<string>>([]);
  const [duplicateInvite, setDuplicateInvite] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutateAsync: createWorkspace } = useCreate<Workspace>();
  const { mutateAsync: createInvite } = useCreate<UserInviteResponse>();

  const handleRemoveInvite = (email: string) => {
    setInviteUsers((prev) => prev.filter((u) => u !== email));
  };

  const handleAddMember = () => {
    if (usageCount + inviteUsers.length >= quotaLimit) return;
    if (!inviteEmail.trim()) return;
    if (inviteUsers.includes(inviteEmail.trim())) {
      setDuplicateInvite(true);
      return;
    }
    setInviteUsers((prev) => [...prev, inviteEmail.trim()]);
    setDuplicateInvite(false);
    setInviteEmail("");
  };

  const handleCreateWorkspace = async () => {
    if (!workspaceName.trim() || !organizationId) return;

    setIsSubmitting(true);

    try {
      const workspaceResponse = await createWorkspace({
        resource: `api/${organizationId}/workspaces`,
        values: {
          name: workspaceName,
        },
      });

      const newWorkspaceId = workspaceResponse?.data?.id;

      if (newWorkspaceId && inviteUsers.length > 0) {
        for (const email of inviteUsers) {
          await createInvite({
            resource: `api/${organizationId}/user-invites`,
            values: {
              recipient: email,
              workspace_roles: {
                [newWorkspaceId]: ["Organization Project Creator"],
              },
              organization_roles: ["Organization Project Creator"],
              expiry: dayjs().add(USER_INVITE_EXPIRY_DAYS, "day").toISOString(),
            },
          });
        }
      }

      setWorkspaceName("");
      setInviteUsers([]);
      setInviteEmail("");
      await refetchQuota();
      onSuccess();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      styles={{
        content: { backgroundColor: theme.colorModalBgDesignSystem },
        header: { alignItems: "flex-start" },
      }}
      className="workspace-modal"
      onCancel={onCancel}
      title={
        <Typography.Title
          style={{
            fontSize: 20,
            backgroundColor: theme.colorModalBgDesignSystem,
            color: theme.colorTextDesignSystem,
            margin: 0,
          }}
        >
          {t("media.workspaces.index.createWorkspace")}
        </Typography.Title>
      }
      footer={null}
    >
      <Flex vertical gap={20}>
        <Flex vertical>
          <Typography.Title
            style={{ fontSize: 14, fontWeight: 500, color: "#000" }}
          >
            {t("media.workspaces.index.workspaceName")}
          </Typography.Title>
          <Input
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
          />
        </Flex>

        {isWorkspaceInvitesEnabled && (
          <>
            <Flex vertical>
              <Typography.Title
                style={{
                  fontSize: 16,
                  fontWeight: 400,
                  color: "#000",
                  lineHeight: "18px",
                }}
              >
                {t("media.workspaces.index.inviteMembers")}
              </Typography.Title>
              <Flex gap={35}>
                <Input
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  disabled={usageCount + inviteUsers.length >= quotaLimit}
                />
                <Button
                  onClick={handleAddMember}
                  disabled={
                    !isValidEmail(inviteEmail) ||
                    usageCount + inviteUsers.length >= quotaLimit
                  }
                >
                  {t("media.workspaces.index.addMember")}
                </Button>
              </Flex>
              <Typography.Text type="danger">
                {duplicateInvite &&
                  t("media.workspaces.index.duplicatedInvite")}
              </Typography.Text>
              <Typography.Text type="danger">
                {usageCount + inviteUsers.length >= quotaLimit &&
                  t("media.workspaces.index.seatQuotaReached")}
              </Typography.Text>
            </Flex>
            <Flex justify="center" vertical>
              {inviteUsers.map((invite) => (
                <WorkspaceInvite
                  key={invite}
                  user={{
                    email: invite,
                  }}
                  onRemove={() => handleRemoveInvite(invite)}
                />
              ))}
            </Flex>
          </>
        )}
        <Flex justify="center">
          <Button
            type="primary"
            size="large"
            style={{ width: "200px" }}
            shape="round"
            onClick={handleCreateWorkspace}
            loading={isSubmitting}
            disabled={!workspaceName.trim()}
          >
            {t("media.workspaces.index.createWorkspace")}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
